import { apiFetch, type RequestAuth } from './client';

/** Raw gondor version state, as returned per shard. */
export type AppVersionState =
  | 'NEW'
  | 'PUBLISHED'
  | 'BUILD_FAILED'
  | 'STARTING'
  | 'START_FAILED'
  | 'RUNNING'
  | 'UPGRADING_RUNTIME'
  | 'STOPPING'
  | 'STOP_FAILED'
  | 'STOPPED'
  | 'ABANDONED';

export interface VersionShardState {
  state: AppVersionState;
  updatedAt: string;
}

export interface AppVersionItem {
  version: string;
  /** shardId -> state on that shard. States can diverge across shards. */
  shards: Record<string, VersionShardState>;
  /** max(shards[*].updatedAt); the list is sorted by this descending server-side. */
  latestUpdatedAt: string;
}

/** rivendell `GET /dev-portal/apps/{appId}/versions`. */
export interface AppVersionsResponse {
  items: AppVersionItem[];
  degradedShards: string[];
  /** How many versions `excludeStates` kept out of `items`; 0 when nothing was excluded. */
  excludedCount: number;
}

/**
 * Versions of one app. `excludeStates` omits versions sitting in one of those states on every
 * shard they exist on (OCP-2203) — a version RUNNING in one region and STOPPED in another is
 * still returned, with both shard rows. Passing none omits nothing.
 */
export function getAppVersions(
  auth: RequestAuth,
  appId: string,
  excludeStates: readonly AppVersionState[],
): Promise<AppVersionsResponse> {
  const query = excludeStates
    .map((state) => `excludeStates=${encodeURIComponent(state)}`)
    .join('&');
  return apiFetch<AppVersionsResponse>(
    `/dev-portal/apps/${encodeURIComponent(appId)}/versions${query && `?${query}`}`,
    auth,
  );
}
